import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { createSharedAgent } from '../../api/agents';
import { queryKeys } from '../../api/queryKeys';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { humanizeError } from '../../lib/errors';

export interface CreateSharedAgentModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly role: 'analyst' | 'developer';
}

const TEXT = {
  analyst: {
    title: 'Создать AI-аналитика',
    defaultName: 'Аналитик Tasks!',
    description:
      'Один AI-аналитик обслуживает несколько проектов. Его настраивает Admin; ' +
      'Project Admin добавляет агента в свой проект на вкладке «AI-агенты».',
    success: 'AI-аналитик создан. Настройте сервер и LLM',
  },
  developer: {
    title: 'Создать AI-разработчика',
    defaultName: 'Разработчик Tasks!',
    description:
      'Один AI-разработчик обслуживает несколько проектов. Его настраивает ' +
      'Admin; Project Admin добавляет агента в свой проект на вкладке «AI-агенты».',
    success: 'AI-разработчик создан. Настройте сервер и LLM',
  },
} as const;

/** Создание singleton'а общего AI-агента (docs/17 §12.1, docs/18 §5.1). */
export function CreateSharedAgentModal({ open, onClose, role }: CreateSharedAgentModalProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [displayName, setDisplayName] = useState<string>(TEXT[role].defaultName);
  const [error, setError] = useState<string | null>(null);
  const text = TEXT[role];

  useEffect(() => {
    if (open) setDisplayName(text.defaultName);
  }, [open, text.defaultName]);

  const mutation = useMutation({
    mutationFn: () =>
      createSharedAgent({
        agentRole: role,
        ...(displayName.trim() === '' ? {} : { displayName: displayName.trim() }),
      }),
    onSuccess: (agent) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.agents.list });
      toast.success(text.success, {
        durationMs: 8_000,
        action: { label: 'Настроить', onClick: () => navigate(`/agents/${agent.id}`) },
      });
      onClose();
    },
    onError: (cause: unknown) => {
      const human = humanizeError(cause);
      setError(human.message);
      toast.error(human.message, { code: human.code });
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={text.title}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" loading={mutation.isPending} onClick={() => mutation.mutate()}>
            Создать
          </Button>
        </>
      }
    >
      <p>{text.description}</p>
      <Input
        label="Имя агента"
        value={displayName}
        error={error}
        onChange={(event) => setDisplayName(event.target.value)}
      />
    </Modal>
  );
}
